import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import {
    FIELD_BASE_CLASSES,
    FIELD_ERROR_BORDER_CLASSES,
    FIELD_ERROR_TEXT_CLASSES,
    FIELD_FOCUS_CLASSES,
    FIELD_HINT_CLASSES,
    FIELD_LABEL_CLASSES,
} from './fieldStyles';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    hint?: string;
    error?: string;
    leadingIcon?: ReactNode;
    trailingSlot?: ReactNode;
    /** Layout only (width, margin) — applied to the wrapper, never the control. */
    className?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
    { label, hint, error, leadingIcon, trailingSlot, className, id, ...rest },
    ref,
) {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

    return (
        <div className={className}>
            {label && (
                <label htmlFor={inputId} className={FIELD_LABEL_CLASSES}>
                    {label}
                </label>
            )}
            <div className="relative flex items-center">
                {leadingIcon && (
                    <span className="pointer-events-none absolute left-3 flex items-center text-content-muted">
                        {leadingIcon}
                    </span>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    className={cn(
                        FIELD_BASE_CLASSES,
                        FIELD_FOCUS_CLASSES,
                        error && FIELD_ERROR_BORDER_CLASSES,
                        leadingIcon && 'pl-9',
                        trailingSlot && 'pr-9',
                    )}
                    {...rest}
                />
                {trailingSlot && (
                    <span className="absolute right-3 flex items-center">{trailingSlot}</span>
                )}
            </div>
            {error ? (
                <p id={errorId} className={FIELD_ERROR_TEXT_CLASSES}>
                    {error}
                </p>
            ) : hint ? (
                <p id={hintId} className={FIELD_HINT_CLASSES}>
                    {hint}
                </p>
            ) : null}
        </div>
    );
});

export default Input;
